import { useEffect, useState } from 'react'
import { registerSW } from 'virtual:pwa-register'

/**
 * Service-worker lifecycle UI. With registerType 'prompt' the new worker
 * waits until the user opts in, so a deploy never swaps assets out from
 * under an active session.
 */
export default function UpdatePrompt() {
  const [needRefresh, setNeedRefresh] = useState(false)
  const [updateFn, setUpdateFn] = useState<(() => Promise<void>) | null>(null)

  useEffect(() => {
    const update = registerSW({
      onNeedRefresh() { setNeedRefresh(true) },
      // Surface new deploys quickly without ever auto-reloading ('prompt'
      // mode is the point — no swap under in-progress work): check on every
      // return to the tab (visibility/focus, throttled to once a minute so
      // rapid alt-tabbing doesn't hammer the network) plus an hourly poll
      // for sessions that never leave the tab. Before this, a long-open tab
      // could sit several deploys behind with no prompt for up to an hour.
      onRegisteredSW(_swUrl, r) {
        if (!r) return
        const check = () => { r.update().catch(() => {}) }
        setInterval(check, 60 * 60 * 1000)
        let lastCheck = 0
        const onWake = () => {
          if (document.visibilityState !== 'visible') return
          const now = Date.now()
          if (now - lastCheck < 60_000) return
          lastCheck = now
          check()
        }
        document.addEventListener('visibilitychange', onWake)
        window.addEventListener('focus', onWake)
      },
    })
    setUpdateFn(() => () => update(true))
  }, [])

  if (!needRefresh) return null

  return (
    <div
      role="status"
      className="fixed top-20 left-1/2 -translate-x-1/2 z-50 card-brutal bg-surface px-4 py-3 shadow-brutal flex items-center gap-3"
      data-testid="sw-update-prompt"
    >
      <span className="font-bold text-sm">A new version of LifeOdds is ready.</span>
      <button
        onClick={async () => { try { await updateFn?.() } finally { window.location.reload() } }}
        data-testid="sw-update-btn"
        className="px-3 py-1.5 rounded-xl bg-primary text-white font-bold text-sm border-2 border-edge shadow-brutal-sm hover:-translate-y-0.5 hover:shadow-brutal transition-all"
      >
        Refresh
      </button>
      <button
        onClick={() => setNeedRefresh(false)}
        aria-label="Dismiss update notification"
        className="text-ink-muted font-bold text-sm hover:text-ink"
      >
        Later
      </button>
    </div>
  )
}
